/**
 * Picks a month, a quarter, a year or any range of days, and steps through them with ‹ › - the
 * way a ring binder is leafed through tab by tab.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  customPeriod,
  monthPeriod,
  nextPeriod,
  previousPeriod,
  quarterPeriod,
  yearPeriod,
  type Period,
} from "@/lib/at/periods";
import { modeOf, periodLabel, type PeriodMode as Mode } from "@/lib/utils/period-label";

export function PeriodPicker({
  value,
  onChange,
}: {
  value: Period;
  onChange: (period: Period) => void;
}) {
  const { t } = useTranslation();
  const mode = modeOf(value);
  const month = Number(value.from.slice(5, 7));

  function switchMode(next: Mode) {
    if (next === "month") onChange(monthPeriod(value.year, month));
    if (next === "quarter") onChange(quarterPeriod(value.year, Math.ceil(month / 3)));
    if (next === "year") onChange(yearPeriod(value.year));
    if (next === "custom") onChange(customPeriod(value.from, value.to));
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="period-picker">
      <Select value={mode} onValueChange={(next) => switchMode(next as Mode)}>
        <SelectTrigger className="w-40" aria-label={t("close.period")} data-testid="period-mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="month">{t("close.month")}</SelectItem>
          <SelectItem value="quarter">{t("close.quarter")}</SelectItem>
          <SelectItem value="year">{t("close.wholeYear")}</SelectItem>
          <SelectItem value="custom">{t("close.custom")}</SelectItem>
        </SelectContent>
      </Select>

      {mode === "custom" ? (
        <>
          <Input
            type="date"
            className="w-40"
            aria-label={t("close.from")}
            value={value.from}
            onChange={(event) =>
              event.target.value && onChange(customPeriod(event.target.value, value.to))
            }
            data-testid="period-from"
          />
          <Input
            type="date"
            className="w-40"
            aria-label={t("close.to")}
            value={value.to}
            onChange={(event) =>
              event.target.value && onChange(customPeriod(value.from, event.target.value))
            }
            data-testid="period-to"
          />
        </>
      ) : (
        <div className="flex items-center rounded-md border bg-card">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("close.previous")}
            onClick={() => onChange(previousPeriod(value))}
            data-testid="period-previous"
          >
            <ChevronLeft />
          </Button>
          <span
            className="min-w-36 px-2 text-center text-sm font-medium"
            data-testid="period-label"
          >
            {periodLabel(value, t)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("close.next")}
            onClick={() => onChange(nextPeriod(value))}
            data-testid="period-next"
          >
            <ChevronRight />
          </Button>
        </div>
      )}
    </div>
  );
}
